import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiMutate } from '../composables/apiMutate'
import { expectArray } from '../composables/expectShape'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'

export type DevcontainerStatus = 'running' | 'idle' | 'stopped'

// Progress of the last-requested devcontainer lifecycle action (#1051).
// Deliberately separate from DevcontainerStatus: a container can be running
// while the action that produced it failed, so "is it up?" and "did the
// action work?" are different questions and the UI has to answer both.
export type LifecycleStatus = 'queued' | 'running' | 'succeeded' | 'failed'

const IN_FLIGHT_LIFECYCLE_STATUSES: ReadonlySet<string> = new Set(['queued', 'running'])

// True while a lifecycle action is still queued or executing on the agent
// box. Used to keep the action buttons disabled — the box runs these one at
// a time, so a second request would just 409.
export function isLifecycleInFlight(repo: AgentRepoStatus): boolean {
  return IN_FLIGHT_LIFECYCLE_STATUSES.has(repo.lifecycle_status ?? '')
}

export interface AgentRepoStatus {
  slug: string
  github_repo: string | null
  is_default: boolean
  source: 'db' | 'yaml'
  token_set: boolean
  registry_token_set: boolean
  checkout_exists: boolean
  devcontainer_exists: boolean
  // Host ports an admin DECLARED for this repo (#1233) — configuration, not a
  // measurement; empty when the repo declares none.
  ports: number[]
  // What the compose files on disk actually publish, measured by the refresh
  // sweep (#1262). null means they couldn't be read, which is NOT the same as
  // [] (publishes nothing) — the two disagreeing with `ports` above is the
  // drift that left the overlap check validating an incomplete picture.
  published_ports: number[] | null
  container_status: DevcontainerStatus
  last_activity_at: string | null
  // Distinct from container_status above: an isolated agent-session (#1352)
  // lives at a global slot path, not this repo's own /workspaces/<slug> —
  // container_status alone reads "stopped" for a repo that's actually
  // mid-session in a slot.
  agent_session_status: DevcontainerStatus
  agent_session_last_activity_at: string | null
  cpu_percent: number | null
  memory_bytes: number | null
  disk_bytes: number | null
  cli_version: string | null
  auto_update_cli: boolean
  last_auto_update_at: string | null
  last_auto_update_status: string | null
  // Resolved (DB override if explicitly set, else YAML) product-owner
  // heartbeat opt-in (#938) — see Agent::RepoRegistry#fetch.
  product_owner_enabled: boolean
  product_owner_cost_ceiling_cents: number | null
  // Resolved per-run ceiling in cents (DB override, else YAML, else
  // Agent::RepoRegistry::DEFAULT_RUN_COST_CEILING_CENTS) — catches a single
  // expensive run the hourly ceiling's trailing-window check structurally
  // cannot (#1181).
  product_owner_run_cost_ceiling_cents: number
  // Resolved check-in cadence in minutes — fully computed by
  // Agent::ProductOwnerCadence on every run (#1407), no longer settable
  // here or via YAML (#1408). Read-only.
  product_owner_poll_interval_minutes: number
  // How slow check-ins may get at most; the only admin-editable cadence field.
  product_owner_max_interval_minutes: number
  // AgentRepo's own persisted aasm cadence state (#1536) — one of
  // 'unstarted' | 'growing' | 'flat' | 'shrinking' | 'bursting' | 'idle'.
  // null for a yaml-only repo with no db row at all yet.
  product_owner_cadence_state: string | null
  // When the state above was last actually entered (#1625) — null until the
  // row's first real transition.
  product_owner_cadence_state_entered_at: string | null
  // Git state of the checkout under /workspaces (#1248), measured host-side by
  // the same sweep as the metrics above. Nothing on the box fetches on a
  // schedule, so git_ahead/git_behind are only as current as git_fetched_at —
  // the page shows them together for that reason. All null when the checkout
  // can't be read, or before the first sweep.
  git_branch: string | null
  git_default_branch: string | null
  git_dirty: boolean | null
  git_ahead: number | null
  git_behind: number | null
  git_fetched_at: string | null
  // Resolved in-container cwd (#949) — defaults to the host workspace path
  // (`/workspaces/<slug>`); only differs for a repo whose own devcontainer
  // mounts its checkout elsewhere (e.g. `/app`).
  container_workspace_folder: string
  // In-flight or last-finished lifecycle action (#1051). These arrive on the
  // page's existing status poll — the POST only returns 202, so this is how
  // the outcome of a start/stop/reboot/rebuild/update_cli gets reported.
  lifecycle_action: string | null
  lifecycle_status: LifecycleStatus | null
  lifecycle_started_at: string | null
  lifecycle_finished_at: string | null
  lifecycle_error: string | null
  // Whether the running container's image predates a later `.devcontainer/**`
  // change (#1123) — null while stopped (nothing to compare) or genuinely
  // unknown (no baseline recorded yet and the tree can't be read).
  devcontainer_stale: boolean | null
  // When the agent box last measured the fields above. They are no longer read
  // live per request — a job on the box refreshes them roughly once a minute —
  // so the page shows their age rather than implying they are current. Null
  // until the first sweep after a deploy.
  stats_refreshed_at: string | null
  // Why the last measurement failed, if it did. Surfaced so a repo whose stats
  // are frozen says so, instead of showing convincing but stale numbers.
  stats_error: string | null
}

interface ReposResponse {
  repos: AgentRepoStatus[]
  latest_cli_version: string | null
}

export interface CreateRepoParams {
  slug: string
  github_repo?: string
  github_token?: string
  registry_token?: string
  is_default?: boolean
  ports?: number[]
}

export interface UpdateRepoParams {
  github_repo?: string
  github_token?: string
  registry_token?: string
  is_default?: boolean
  auto_update_cli?: boolean
  product_owner_enabled?: boolean
  product_owner_cost_ceiling_cents?: number
  product_owner_run_cost_ceiling_cents?: number
  product_owner_max_interval_minutes?: number
  container_workspace_folder?: string
  ports?: number[]
}

// Admin store for the agent repo registry. Manages the list of repos
// that the cloud agent can work on, overlaying the committed YAML config with
// DB-persisted rows (token stored encrypted, never returned).
export const useAgentReposStore = defineStore('agentRepos', () => {
  const latestCliVersion = ref<string | null>(null)
  const {
    data: repos,
    loaded,
    loading,
    error: loadError,
    needsAuth,
    refresh: fetchAll,
  } = useAsyncResource<AgentRepoStatus[]>(
    async () => {
      const res = await fetch('/api/v1/agent/repos', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load repos: ${res.status}`)
      const data = (await res.json()) as ReposResponse
      latestCliVersion.value = data.latest_cli_version
      return expectArray<AgentRepoStatus>(data.repos, 'the agent box')
    },
    { initialValue: [] },
  )

  async function create(params: CreateRepoParams): Promise<void> {
    await apiMutate('/api/v1/agent/repos', {
      method: 'POST',
      body: { agent_repo: params },
      failureLabel: 'Failed to add repo',
    })
    await fetchAll()
  }

  async function update(slug: string, params: UpdateRepoParams): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: { agent_repo: params },
      failureLabel: 'Failed to update repo',
    })
    await fetchAll()
  }

  async function destroy(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      failureLabel: 'Failed to remove repo',
    })
    await fetchAll()
  }

  // Start/stop a repo's devcontainer (#844).
  //
  // All five lifecycle calls below now return 202 Accepted, not a completed
  // result (#1051) — the agent box runs them as a serialized background job
  // because they take minutes, far longer than the relay's 15s timeout or
  // Cloudflare's ~100s proxy cap. Resolving here means "accepted", NOT
  // "done". The immediate fetchAll picks up `lifecycle_status: 'queued'` so
  // the row reflects the request at once; the page's existing status poll
  // carries it through running → succeeded/failed.
  //
  // A 409 (another action already in flight for this repo) surfaces through
  // apiMutate's normal throw path with the agent box's message.
  async function start(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/start`, {
      method: 'POST',
      failureLabel: 'Failed to start devcontainer',
    })
    await fetchAll()
  }

  async function stop(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/stop`, {
      method: 'POST',
      failureLabel: 'Failed to stop devcontainer',
    })
    await fetchAll()
  }

  // Removes the container(s) and, for a compose devcontainer, the compose
  // network — rather than just stopping them like #stop (#1235). The
  // recovery path for a broken network: every other lifecycle action
  // recreates containers onto whatever's left of the existing one.
  async function destroyContainer(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/destroy_container`, {
      method: 'POST',
      failureLabel: 'Failed to remove devcontainer',
    })
    await fetchAll()
  }

  // Recreates the container from its current image (#845) — the cheapest of
  // the three recreate-style actions (reboot/rebuild/rebuild-without-cache).
  async function reboot(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/reboot`, {
      method: 'POST',
      failureLabel: 'Failed to reboot devcontainer',
    })
    await fetchAll()
  }

  // Rebuilds the devcontainer image (optionally bypassing Docker's build
  // cache), then recreates the container against it (#846).
  async function rebuild(slug: string, noCache: boolean): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/rebuild`, {
      method: 'POST',
      body: { no_cache: noCache },
      failureLabel: 'Failed to rebuild devcontainer',
    })
    await fetchAll()
  }

  // Refreshes Claude Code inside the running devcontainer, in place and
  // ephemerally (#847) — non-destructive, no confirm-if-active needed.
  async function updateCli(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/update_cli`, {
      method: 'POST',
      failureLabel: 'Failed to update Claude Code CLI',
    })
    await fetchAll()
  }

  // Re-measures one repo's cached stats now rather than waiting for the agent
  // box's next sweep. 202 like the lifecycle actions above — resolving means
  // "accepted", and the new numbers arrive on the page's next status poll.
  async function refreshStats(slug: string): Promise<void> {
    await apiMutate(`/api/v1/agent/repos/${encodeURIComponent(slug)}/refresh_stats`, {
      method: 'POST',
      failureLabel: 'Failed to refresh metrics',
    })
  }

  return {
    repos,
    latestCliVersion,
    loading,
    loaded,
    needsAuth,
    loadError,
    fetchAll,
    create,
    update,
    destroy,
    start,
    stop,
    destroyContainer,
    reboot,
    rebuild,
    updateCli,
    refreshStats,
  }
})
